import { Fragment, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { RING_STATUS } from "score-common";
import { useActiveOrg, userDisplayName } from "common-fe";
import { useScoreApi } from "../api/scoreApi";
import useOrgMembersById from "../hooks/useOrgMembersById";
import { classNumberLabel } from "../util/classDisplay";
import SetRingStateDialog from "./SetRingStateDialog";

// #1936 (docs/score/ring-state.md) — the Ring section: every ring in the
// show, in #1909's `ringsFor` order, with what's in it, where that value
// came from, and one-click controls to fix it. This is the screen a person
// opens when a board is showing the wrong thing during a live class (the
// card's framing), so every action here is a single click/dialog-submit —
// Clear costs no more steps than Set did (AC5), and Break/End of day are
// one PUT each with no class/ride (docs/score/ring-state.md: "carry
// neither").
//
// Clearing uses DELETE /ring-state/:ring (scoreApi.ringState.clearRingState),
// never a client-reconstructed "derived" guess PUT back as EXPLICIT — an
// absent row already IS the automatic state (AC7), and the next scoring
// write recreates it fresh via mergeRingStateOnScore's `!existing` branch.
function formatSetAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  // #3630 -- short, month/day and time, so it fits a small second line.
  return d.toLocaleString([], { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// #2075 — a ring-state row's `setBy` is a raw Cognito sub; it must never
// reach the screen as-is (bug #182). Resolve through the org's member map
// (useOrgMembersById) to a "First Last"/email display name (common-fe's
// userDisplayName, #736's helper), and fall back to a neutral label — never
// an empty cell, never the sub itself — when the sub isn't a current org
// member (a former user, or a row a service wrote).
function setByLabel(setBy, membersById, unknownLabel) {
  if (!setBy) return null;
  return userDisplayName(membersById[setBy]) || unknownLabel;
}

// Semantic MUI palette colors (success/warning/default), not "primary" —
// primary reads as invisible grey in dark mode (CLAUDE.md UI convention).
function statusChipProps(status) {
  switch (status) {
    case RING_STATUS.RUNNING:
      return { color: "success", variant: "filled" };
    case RING_STATUS.BREAK:
      return { color: "warning", variant: "filled" };
    case RING_STATUS.END_OF_DAY:
      return { color: "default", variant: "outlined" };
    case RING_STATUS.CLASS_ENDED:
      return { color: "info", variant: "outlined" };
    default:
      return { color: "default", variant: "outlined" };
  }
}

// Static i18n keys as whole literal strings so i18next-parser and the
// missing-key audit can see them (CLAUDE.md: no dynamic i18n keys).
const STATUS_LABEL_KEYS = {
  [RING_STATUS.RUNNING]: "ringState.status.RUNNING",
  [RING_STATUS.BREAK]: "ringState.status.BREAK",
  [RING_STATUS.END_OF_DAY]: "ringState.status.END_OF_DAY",
  [RING_STATUS.CLASS_ENDED]: "ringState.status.CLASS_ENDED",
};

// #3630 -- the fixed column grid: Ring | Status | Class | Ride | Set | Actions.
const RING_COLUMN_WIDTHS = ["13%", "9%", "18%", "12%", "14%", "34%"];
const RING_TABLE_MIN_WIDTH = 940;
// Set | Break | End of day | Clear: one fixed track each, so a row without a
// Clear button still reserves its slot.
const ACTION_GRID_COLUMNS = "54px 66px 106px 60px";
const ACTION_BUTTON_SX = { minWidth: 0, height: 28, px: 1, width: "100%", whiteSpace: "nowrap", lineHeight: 1 };
const CELL_ONE_LINE = { overflow: "hidden", textOverflow: "ellipsis" };

// #3630 -- the class chip under the class name is only worth a line when it
// says something the ring Status chip does not. A running class in a Running
// ring (or a finished class in a Class ended ring) repeats the Status column.
function showClassStateChip(row) {
  if (row.classRunning == null) return false;
  if (row.classRunning) return row.status !== RING_STATUS.RUNNING;
  return row.status !== RING_STATUS.CLASS_ENDED;
}

// #2365 — `ringState` is ShowDashboard's single useRingStates(showId) call,
// shared with ScribePresenceSection so the classes/rides/ring-state fetch
// happens once per mount, not once per section.
export default function RingStateSection({ showId, ringState }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const { rows, classes, ridesByClassId, loading, error, refetch } = ringState;
  const { activeOrgId } = useActiveOrg();
  const membersById = useOrgMembersById(activeOrgId);

  const [setDialogRing, setSetDialogRing] = useState(null);
  const [busyRing, setBusyRing] = useState(null);
  const [rowErrors, setRowErrors] = useState({});

  const clearRowError = (ring) =>
    setRowErrors((prev) => {
      if (!(ring in prev)) return prev;
      const next = { ...prev };
      delete next[ring];
      return next;
    });

  const runAction = async (ring, fn) => {
    setBusyRing(ring);
    clearRowError(ring);
    try {
      await fn();
      await refetch();
    } catch (err) {
      const code = err?.response?.data?.code;
      const message =
        code === "running_requires_class"
          ? t("ringState.errors.runningRequiresClass")
          : err?.serverErrorDetail || err?.message || t("ringState.errors.generic");
      setRowErrors((prev) => ({ ...prev, [ring]: message }));
    } finally {
      setBusyRing(null);
    }
  };

  const handleBreak = (ring) =>
    runAction(ring, () =>
      scoreApi.ringState.setRingState({
        showId,
        ring,
        status: RING_STATUS.BREAK,
        classId: null,
        rideId: null,
      })
    );

  const handleEndOfDay = (ring) =>
    runAction(ring, () =>
      scoreApi.ringState.setRingState({
        showId,
        ring,
        status: RING_STATUS.END_OF_DAY,
        classId: null,
        rideId: null,
      })
    );

  const handleClear = (ring) =>
    runAction(ring, () => scoreApi.ringState.clearRingState(showId, ring));

  if (!showId) return null;

  return (
    <Box data-testid="ring-state-section">
      {/* #3630 -- a small section title sitting right on its table. */}
      <Typography variant="subtitle1" component="h2" color="text.primary" sx={{ mb: 0.5, fontWeight: 500 }}>
        {t("ringState.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="ring-state-load-error">
          {t("ringState.loadError")}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">{t("ringState.noRings")}</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
          {/* #3630 -- fixed column widths so every column lines up row to row
              (colgroup + table-layout: fixed). Long ring and class names
              ellipsize inside their column instead of pushing the others. */}
          <Table
            size="small"
            data-testid="ring-state-table"
            sx={{ minWidth: RING_TABLE_MIN_WIDTH, "& td, & th": { px: 1.25, py: 0.75 } }}
            style={{ tableLayout: "fixed" }}
          >
            <colgroup data-testid="ring-state-colgroup">
              {RING_COLUMN_WIDTHS.map((width, i) => (
                <col key={i} style={{ width }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>{t("ringState.columns.ring")}</TableCell>
                <TableCell>{t("ringState.columns.status")}</TableCell>
                <TableCell>{t("ringState.columns.class")}</TableCell>
                <TableCell>{t("ringState.columns.ride")}</TableCell>
                <TableCell>{t("ringState.columns.set")}</TableCell>
                <TableCell>{t("ringState.columns.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const busy = busyRing === row.ring;
                const statusProps = statusChipProps(row.status);
                const statusLabel = t(STATUS_LABEL_KEYS[row.status] ?? "ringState.status.NONE");
                const setAtLabel = formatSetAt(row.setAt);
                const setByDisplay = setByLabel(row.setBy, membersById, t("common.unknownUser"));
                const classLabel = row.class ? classNumberLabel(row.class) : null;
                const rideLabel = row.ride
                  ? row.ride.riderName || row.ride.horseName || t("common.unknownUser")
                  : null;

                return (
                  <Fragment key={row.ring}>
                    <TableRow data-testid={`ring-row-${row.ring}`}>
                      <TableCell sx={CELL_ONE_LINE}>
                        <Typography variant="body2" color="text.primary" noWrap title={row.ring}>
                          {row.ring}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusLabel}
                          data-testid={`ring-status-${row.ring}`}
                          {...statusProps}
                        />
                      </TableCell>
                      <TableCell sx={CELL_ONE_LINE}>
                        {row.class ? (
                          <Stack spacing={0.5} alignItems="flex-start">
                            <Typography variant="body2" color="text.primary" noWrap title={classLabel} sx={{ maxWidth: "100%" }}>
                              {classLabel}
                            </Typography>
                            {showClassStateChip(row) && (
                              <Chip
                                size="small"
                                variant="outlined"
                                color={row.classRunning ? "info" : "default"}
                                data-testid={`ring-class-state-${row.ring}`}
                                label={
                                  row.classRunning
                                    ? t("ringState.classState.running")
                                    : t("ringState.classState.finished")
                                }
                              />
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t("ringState.noClass")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={CELL_ONE_LINE}>
                        {rideLabel ? (
                          <Typography variant="body2" color="text.primary" noWrap title={rideLabel}>
                            {rideLabel}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t("ringState.noRide")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={CELL_ONE_LINE}>
                        {/* #3630 -- source and "set at / by" are ONE Set column:
                            the source on top, the time (and who) on a small
                            second line. */}
                        {row.hasOverride ? (
                          <Chip
                            size="small"
                            color={row.isExplicit ? "warning" : "default"}
                            variant={row.isExplicit ? "filled" : "outlined"}
                            label={
                              row.isExplicit
                                ? t("ringState.source.explicit")
                                : t("ringState.source.automatic")
                            }
                            data-testid={`ring-source-${row.ring}`}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            data-testid={`ring-source-${row.ring}`}
                          >
                            {t("ringState.source.none")}
                          </Typography>
                        )}
                        {(setAtLabel || setByDisplay) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            noWrap
                            title={[setAtLabel, setByDisplay].filter(Boolean).join(" \u00b7 ")}
                          >
                            {setAtLabel}
                            {setAtLabel && setByDisplay ? " \u00b7 " : ""}
                            {setByDisplay && (
                              <span data-testid={`ring-set-by-${row.ring}`}>{setByDisplay}</span>
                            )}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {/* #2076/bug #185, #3630 -- four fixed slots in a grid:
                            Set, Break, End of day, then Clear. Clear always
                            has its own slot (empty when there is nothing to
                            clear), so the three buttons sit in the same spot
                            on every row. */}
                        <Box
                          data-testid={`ring-actions-${row.ring}`}
                          sx={{ display: "grid", gridTemplateColumns: ACTION_GRID_COLUMNS, gap: 0.5, justifyContent: "start" }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            sx={ACTION_BUTTON_SX}
                            disabled={busy}
                            data-testid={`ring-action-set-${row.ring}`}
                            onClick={() => setSetDialogRing(row.ring)}
                          >
                            {t("ringState.actions.set")}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={ACTION_BUTTON_SX}
                            disabled={busy}
                            data-testid={`ring-action-break-${row.ring}`}
                            onClick={() => handleBreak(row.ring)}
                          >
                            {t("ringState.actions.break")}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            sx={ACTION_BUTTON_SX}
                            disabled={busy}
                            data-testid={`ring-action-endofday-${row.ring}`}
                            onClick={() => handleEndOfDay(row.ring)}
                          >
                            {t("ringState.actions.endOfDay")}
                          </Button>
                          <Box data-testid={`ring-action-clear-slot-${row.ring}`}>
                            {row.hasOverride && (
                              <Button
                                size="small"
                                variant="text"
                                color="error"
                                sx={ACTION_BUTTON_SX}
                                disabled={busy}
                                data-testid={`ring-action-clear-${row.ring}`}
                                onClick={() => handleClear(row.ring)}
                              >
                                {busy ? <CircularProgress size={16} /> : t("ringState.actions.clear")}
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {rowErrors[row.ring] && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ pt: 0, pb: 1, border: 0 }}>
                          <Alert severity="error" data-testid={`ring-error-${row.ring}`}>
                            {rowErrors[row.ring]}
                          </Alert>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <SetRingStateDialog
        open={!!setDialogRing}
        ring={setDialogRing}
        showId={showId}
        classes={classes}
        ridesByClassId={ridesByClassId}
        onClose={() => setSetDialogRing(null)}
        onSaved={() => {
          setSetDialogRing(null);
          refetch();
        }}
      />
    </Box>
  );
}
